import { useContext, useState } from 'react';

import { useRouter } from 'next/router';

import {
	AutoOpen,
	getVideoTutorials,
	VideoTutorialsResponse,
} from '@/api/video-tutorials/video-tutorials';
import { UserContext } from '@/context/user.context';

import useAsyncEffect from './use-async-effect';

type Props = {
	setIsOpenModal: (val: boolean) => void;
};

type Result = {
	/** Видео инструкция */
	videoData: VideoTutorialsResponse | null;
	/** Статус загрузки */
	isLoading: boolean;
};

/** Хук для работы с видео инструкциями.
 *
 * @param setIsOpenModal - Функция для изменения стейта, отвечающего за показ модального окна.
 *
 * @returns Информация о видео инструкции и статусе загрузка {@link Result}
 */
export const useVideoHelper = ({ setIsOpenModal }: Props): Result => {
	const [isLoading, setIsLoading] = useState(true);
	const [videoData, setVideoData] = useState<VideoTutorialsResponse>(null);
	const router = useRouter();
	const { user } = useContext(UserContext);
	const tutorialKey = `${router.pathname}-${user?.id}`;

	useAsyncEffect(async () => {
		setIsOpenModal(false);
		try {
			const data = await getVideoTutorials(router.pathname === '/' ? '/home' : router.pathname);
			setVideoData(data);

			if (data.autoOpen === AutoOpen.Always) {
				setIsOpenModal(true);
			}

			if (data.autoOpen === AutoOpen.Once) {
				const updatedConfig: string = localStorage.getItem(tutorialKey);

				if (!updatedConfig || updatedConfig !== data.updated) {
					setIsOpenModal(true);
				}
			}
		} catch (e) {
			console.error(e);
			setVideoData(null);
		} finally {
			setIsLoading(false);
		}
	}, [router.pathname]);

	return {
		videoData,
		isLoading,
	};
};
